import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Droplets, Heart, Sparkles } from 'lucide-react';

const BrandStorySection = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["50px", "-50px"]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  // Floating bubbles animation component
  const FloatingBubble = ({ delay = 0, x = "50%", size = "w-4 h-4", duration = 3 }) => (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ 
        y: [0, -20, 0],
        opacity: [0.3, 0.8, 0.3]
      }}
      transition={{
        delay,
        duration,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className={`absolute ${size} bg-mava-gradient rounded-full blur-sm`}
      style={{ left: x, bottom: "10%" }}
    />
  );

  return (
    <motion.section
      ref={containerRef}
      style={{ y, opacity }}
      className="relative py-20 lg:py-32 bg-gradient-to-b from-white to-mava-off-white overflow-hidden"
      id="about"
    >
      {/* Background flowing water lines */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ 
            backgroundPosition: ["0% 0%", "100% 100%"]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(45deg, transparent 40%, rgba(0, 198, 255, 0.3) 50%, transparent 60%),
              linear-gradient(-45deg, transparent 40%, rgba(0, 114, 255, 0.3) 50%, transparent 60%)
            `,
            backgroundSize: "200px 200px"
          }}
        />
      </div>
      {/* Floating bubbles */}
      <FloatingBubble delay={0} x="15%" size="w-3 h-3" duration={4} />
      <FloatingBubble delay={1} x="85%" size="w-6 h-6" duration={3} />
      <FloatingBubble delay={2} x="25%" size="w-4 h-4" duration={5} />
      <FloatingBubble delay={3} x="75%" size="w-5 h-5" duration={3.5} />
      <FloatingBubble delay={1.5} x="60%" size="w-3 h-3" duration={4.5} />
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-flex items-center justify-center w-16 h-16 bg-mava-gradient rounded-full mb-6 liquid-shadow"
            >
              <Heart className="w-8 h-8 text-white" />
            </motion.div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold text-mava-charcoal mb-6">
              Our Story
            </h2>
            <div className="w-24 h-1 bg-mava-gradient mx-auto rounded-full" />
          </motion.div>

          {/* Main Story Content */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-12"
          >
            <motion.p
              className="text-2xl md:text-3xl lg:text-4xl font-poppins font-semibold text-mava-orange mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
            >
              "From pure water to crafted beverages — Mava stands for refreshment."
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="max-w-3xl mx-auto"
            >
              <p className="text-lg md:text-xl font-inter text-gray-700 leading-relaxed mb-6">
                Mava Cold Beverages was born from a simple belief: that refreshment should be pure, 
                authentic, and accessible to everyone. What started as a commitment to delivering 
                the purest bottled water has evolved into a vision of creating a complete range 
                of beverages that celebrate Indian flavors and global standards.
              </p>

              <p className="text-lg md:text-xl font-inter text-gray-700 leading-relaxed">
                Today, we're not just a water company — we're a lifestyle brand that stands for 
                purity, freshness, and the youthful energy that drives us forward. From our signature 
                pure water to our exciting upcoming range of Jeera Drink, Cola, Mango Juice, and 
                Goli Soda, every product reflects our dedication to quality and innovation.
              </p>
            </motion.div>
          </motion.div>

          {/* Key Values */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Droplets,
                title: "Purity",
                description: "Every drop is filtered to perfection, ensuring the highest quality standards"
              },
              {
                icon: Sparkles,
                title: "Innovation",
                description: "Crafting beverages that blend traditional flavors with modern taste"
              },
              {
                icon: Heart,
                title: "Passion",
                description: "Driven by love for quality and commitment to customer satisfaction"
              }
            ]?.map((value, index) => (
              <motion.div
                key={value?.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.2 }}
                className="group"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-2xl p-8 liquid-shadow brand-transition cursor-pointer"
                >
                  <motion.div
                    animate={{ 
                      rotate: [0, 10, -10, 0]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: index * 0.5
                    }}
                    className="inline-flex items-center justify-center w-14 h-14 bg-mava-gradient rounded-full mb-4"
                  >
                    <value.icon className="w-7 h-7 text-white" />
                  </motion.div>

                  <h3 className="text-xl font-poppins font-bold text-mava-charcoal mb-3">
                    {value?.title}
                  </h3>
                  <p className="text-gray-600 font-inter leading-relaxed">
                    {value?.description}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
      {/* Decorative water flow at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-20 overflow-hidden">
        <motion.div
          animate={{ 
            x: ["-100%", "100%"]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-0 w-full h-8 bg-mava-gradient opacity-10"
          style={{
            clipPath: "polygon(0 100%, 100% 100%, 90% 0, 10% 0)"
          }}
        />
      </div>
    </motion.section>
  );
};

export default BrandStorySection;